import type { ElementNode, TextNode } from '@payloadcms/richtext-slate';
import type { UploadedImage } from '@prezly/uploadcare';
import React, { Fragment } from 'react';

import { Link } from '@/components/Link/Link';
import { Image } from '@/src/ui/Image/Image';
import { PriceReplacer } from '@/src/ui/PriceReplacer/PriceReplacer';
import type { Media, Video } from '@/types';

import { Heading } from '../SlateRenderer/components/Heading/Heading';

import { RenderVideo } from './RenderVideo';

type Node = {
    [k: string]: unknown;
};

type Props = {
    className?: string;
    nodes: Node[];
    options?: {
        replaceCurrency?: boolean;
    };
};

// Copied from `@payloadcms/richtext-slate` (https://github.com/payloadcms/payload/blob/main/packages/richtext-slate/src/types.ts#L10-L12).
// Because of a barrel file in the library, other non-browser code is bundled, which throws an exception in brower.
function nodeIsTextNode(node: ElementNode | TextNode): node is TextNode {
    return 'text' in node;
}

export function PayloadSlateRenderer({ className, nodes, options }: Props) {
    return <div className={className}>{renderNodes(nodes, options)}</div>;
}

function renderNodes(children: any[] | undefined, options: Props['options'] = {}) {
    return children?.map((node, i) => {
        if (nodeIsTextNode(node)) {
            let text = options.replaceCurrency ? (
                <span>
                    <PriceReplacer>{node.text}</PriceReplacer>
                </span>
            ) : (
                <span dangerouslySetInnerHTML={{ __html: node.text }} />
            );

            if (node.bold) {
                text = <strong key={i}>{text}</strong>;
            }

            if (node.code) {
                text = <code key={i}>{text}</code>;
            }

            if (node.italic) {
                text = <em key={i}>{text}</em>;
            }

            if (node.underline) {
                text = <u key={i}>{text}</u>;
            }

            if (node.strikethrough) {
                text = <s key={i}>{text}</s>;
            }

            return <Fragment key={i}>{text}</Fragment>;
        }

        if (!node) {
            return null;
        }

        switch (node.type) {
            case 'h1':
            case 'h2':
            case 'h3':
                return (
                    <Heading key={i} level={node.type} node={node}>
                        {renderNodes(node.children)}
                    </Heading>
                );
            case 'h4':
                return <h4 key={i}>{renderNodes(node.children)}</h4>;
            case 'h5':
                return <h5 key={i}>{renderNodes(node.children)}</h5>;
            case 'h6':
                return <h6 key={i}>{renderNodes(node.children)}</h6>;
            case 'blockquote':
                return <blockquote key={i}>{renderNodes(node.children)}</blockquote>;
            case 'ul':
                return <ul key={i}>{renderNodes(node.children)}</ul>;
            case 'ol':
                return <ol key={i}>{renderNodes(node.children)}</ol>;
            case 'li':
                return <li key={i}>{renderNodes(node.children)}</li>;
            case 'link':
                return <RenderLink key={i} link={node} />;
            case 'upload':
                return <RenderUpload key={i} upload={node} />;
            case 'relationship':
                return <RenderRelationship key={i} node={node} />;

            default:
                return <p key={i}>{renderNodes(node.children, options)}</p>;
        }
    });
}

function RenderLink({ link }: any) {
    const linkHref = link?.linkType === 'internal' && link.doc ? link.doc.path : link?.url;
    const shouldOpenInNewTab = link.newTab ?? false;

    return (
        <Link href={linkHref} shouldOpenInNewTab={shouldOpenInNewTab}>
            {renderNodes(link.children)}
        </Link>
    );
}

function RenderUpload({ upload }: { upload: Media }) {
    const image = upload.uploadcare as UploadedImage | undefined;

    if (!image) {
        return null;
    }

    return (
        <Image
            sizes="100vw, (min-width: 992px) 70vw, (min-width: 1280px) 860px"
            loading="lazy"
            src={image}
            alt={upload.filename}
        />
    );
}

function isVideoNode(node: Node): node is Video {
    return node.contentType === 'videos' && Boolean(node.id);
}

function RenderRelationship({ node }: { node: Node }) {
    // TODO: support other relationship fields

    if (isVideoNode(node)) {
        return <RenderVideo id={node.id} />;
    }

    return null;
}
